import { supabase, isSupabaseConfigured, getCurrentUserId } from "@/lib/supabase";
import type { Workflow } from "@/types/aiToolkit";

const WORKFLOW_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isWorkflowUuid(id: string): boolean {
  return WORKFLOW_UUID_RE.test(id);
}

function templateTagFor(templateId: string): string {
  return `tpl:${templateId}`;
}

interface DatabaseWorkflow {
  id: string;
  user_id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  category: string;
  tags: string[];
  access: string;
  trigger: {
    type: string;
    config: Record<string, any>;
  };
  nodes: any[];
  edges: any[];
  entry_node_id?: string;
  variables?: any[];
  is_blueprint?: boolean;
  blueprint_category?: string;
  estimated_duration?: string;
  estimated_cost?: string;
  last_run_at?: string;
  run_count?: number;
  status?: 'draft' | 'active' | 'paused' | 'archived';
  created_at: string;
  updated_at: string;
}

interface InsertWorkflow {
  name: string;
  description: string;
  icon: string;
  color: string;
  category: string;
  tags: string[];
  access: string;
  trigger: {
    type: string;
    config: Record<string, any>;
  };
  nodes: any[];
  edges: any[];
  entryNodeId?: string;
  variables?: any[];
  isBlueprint?: boolean;
  blueprintCategory?: string;
  estimatedDuration?: string;
  estimatedCost?: string;
  lastRunAt?: string;
  runCount?: number;
  status?: 'draft' | 'active' | 'paused' | 'archived';
}

class AIWorkflowsService {
  /**
   * Get all workflows for the current user
   */
  async getWorkflows(): Promise<Workflow[]> {
    if (!isSupabaseConfigured) return [];
    const userId = await getCurrentUserId();
    if (!userId) return [];

    const { data, error } = await supabase
      .from("ai_workflows")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching workflows:", error);
      return [];
    }

    return (data || []).map(this.mapToWorkflow);
  }

  /**
   * Get a single workflow by ID
   */
  async getWorkflowById(id: string): Promise<Workflow | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;

    const { data, error } = await supabase
      .from("ai_workflows")
      .select("*")
      .eq("id", id)
      .eq("user_id", userId)
      .single();

    if (error) {
      console.error("Error fetching workflow:", error);
      return null;
    }

    return data ? this.mapToWorkflow(data) : null;
  }

  /**
   * Resolve in-memory / template workflows (e.g. workflow-template-gmail-spam) to a DB row UUID
   * before creating workflow_runs (workflow_id must be UUID).
   */
  async ensureWorkflowRecord(workflow: Workflow): Promise<string | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;

    if (isWorkflowUuid(workflow.id)) {
      const existing = await this.getWorkflowById(workflow.id);
      if (existing) return workflow.id;
    }

    const tplTag = templateTagFor(workflow.id);
    const { data: existingTpl } = await supabase
      .from("ai_workflows")
      .select("id")
      .eq("user_id", userId)
      .contains("tags", [tplTag])
      .maybeSingle();

    if (existingTpl?.id) {
      await this.updateWorkflow(existingTpl.id as string, {
        nodes: workflow.nodes,
        edges: workflow.edges,
        entryNodeId: workflow.entryNodeId,
        trigger: workflow.trigger,
        status: workflow.status ?? "active",
      });
      return existingTpl.id as string;
    }

    const tags = workflow.tags?.includes(tplTag)
      ? workflow.tags
      : [...(workflow.tags ?? []), tplTag];

    const created = await this.createWorkflow({
      name: workflow.name,
      description: workflow.description,
      icon: workflow.icon,
      color: workflow.color,
      category: workflow.category,
      tags,
      access: workflow.access,
      trigger: workflow.trigger,
      nodes: workflow.nodes,
      edges: workflow.edges,
      entryNodeId: workflow.entryNodeId,
      variables: workflow.variables,
      isBlueprint: workflow.isBlueprint ?? true,
      blueprintCategory: workflow.blueprintCategory,
      status: workflow.status ?? "active",
    });

    return created?.id ?? null;
  }

  /**
   * Create a new workflow
   */
  async createWorkflow(workflow: InsertWorkflow): Promise<Workflow | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;

    const insertPayload: any = {
      user_id: userId,
      name: workflow.name,
      description: workflow.description || "",
      icon: workflow.icon || "GitCompare",
      color: workflow.color || "bg-orange-500",
      category: workflow.category || "Automation",
      tags: workflow.tags || [],
      access: workflow.access || "free",
      trigger: workflow.trigger || { type: "manual", config: {} },
      nodes: workflow.nodes || [],
      edges: workflow.edges || [],
    };

    if (workflow.entryNodeId) {
      insertPayload.entry_node_id = workflow.entryNodeId;
    }
    if (workflow.variables) {
      insertPayload.variables = workflow.variables;
    }
    if (workflow.isBlueprint !== undefined) {
      insertPayload.is_blueprint = workflow.isBlueprint;
    }
    if (workflow.blueprintCategory) {
      insertPayload.blueprint_category = workflow.blueprintCategory;
    }
    if (workflow.estimatedDuration) {
      insertPayload.estimated_duration = workflow.estimatedDuration;
    }
    if (workflow.estimatedCost) {
      insertPayload.estimated_cost = workflow.estimatedCost;
    }
    if (workflow.lastRunAt) {
      insertPayload.last_run_at = workflow.lastRunAt;
    }
    if (workflow.runCount !== undefined) {
      insertPayload.run_count = workflow.runCount;
    }
    if (workflow.status) {
      insertPayload.status = workflow.status;
    }

    const { data, error } = await supabase
      .from("ai_workflows")
      .insert(insertPayload)
      .select()
      .single();

    if (error) {
      console.error("Error creating workflow:", error);
      throw new Error(error.message || "Failed to create workflow");
    }

    return data ? this.mapToWorkflow(data) : null;
  }

  /**
   * Update an existing workflow
   */
  async updateWorkflow(id: string, updates: Partial<InsertWorkflow>): Promise<Workflow | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;

    const updatePayload: any = {
      updated_at: new Date().toISOString(),
    };

    if (updates.name !== undefined) updatePayload.name = updates.name;
    if (updates.description !== undefined) updatePayload.description = updates.description;
    if (updates.icon !== undefined) updatePayload.icon = updates.icon;
    if (updates.color !== undefined) updatePayload.color = updates.color;
    if (updates.category !== undefined) updatePayload.category = updates.category;
    if (updates.tags !== undefined) updatePayload.tags = updates.tags;
    if (updates.access !== undefined) updatePayload.access = updates.access;
    if (updates.trigger !== undefined) updatePayload.trigger = updates.trigger;
    if (updates.nodes !== undefined) updatePayload.nodes = updates.nodes;
    if (updates.edges !== undefined) updatePayload.edges = updates.edges;
    if (updates.entryNodeId !== undefined) updatePayload.entry_node_id = updates.entryNodeId;
    if (updates.variables !== undefined) updatePayload.variables = updates.variables;
    if (updates.isBlueprint !== undefined) updatePayload.is_blueprint = updates.isBlueprint;
    if (updates.blueprintCategory !== undefined) updatePayload.blueprint_category = updates.blueprintCategory;
    if (updates.estimatedDuration !== undefined) updatePayload.estimated_duration = updates.estimatedDuration;
    if (updates.estimatedCost !== undefined) updatePayload.estimated_cost = updates.estimatedCost;
    if (updates.lastRunAt !== undefined) updatePayload.last_run_at = updates.lastRunAt;
    if (updates.runCount !== undefined) updatePayload.run_count = updates.runCount;
    if (updates.status !== undefined) updatePayload.status = updates.status;

    const { data, error } = await supabase
      .from("ai_workflows")
      .update(updatePayload)
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      console.error("Error updating workflow:", error);
      throw new Error(error.message || "Failed to update workflow");
    }

    return data ? this.mapToWorkflow(data) : null;
  }

  /**
   * Delete a workflow
   */
  async deleteWorkflow(id: string): Promise<boolean> {
    if (!isSupabaseConfigured) return false;
    const userId = await getCurrentUserId();
    if (!userId) return false;

    const { error } = await supabase
      .from("ai_workflows")
      .delete()
      .eq("id", id)
      .eq("user_id", userId);

    if (error) {
      console.error("Error deleting workflow:", error);
      throw new Error(error.message || "Failed to delete workflow");
    }

    return true;
  }

  /**
   * Map database row to Workflow type
   */
  private mapToWorkflow(data: DatabaseWorkflow): Workflow {
    return {
      id: data.id,
      name: data.name,
      description: data.description,
      tier: "workflow",
      icon: data.icon,
      color: data.color,
      category: data.category,
      tags: data.tags || [],
      access: data.access as Workflow["access"],
      trigger: data.trigger as Workflow["trigger"],
      nodes: data.nodes || [],
      edges: data.edges || [],
      entryNodeId: data.entry_node_id || (data.nodes && data.nodes.length > 0 ? data.nodes[0].id : ""),
      variables: data.variables,
      isBlueprint: data.is_blueprint || false,
      blueprintCategory: data.blueprint_category,
      estimatedDuration: data.estimated_duration,
      estimatedCost: data.estimated_cost,
      lastRunAt: data.last_run_at,
      runCount: data.run_count || 0,
      status: data.status || "draft",
      createdAt: data.created_at,
      createdBy: "user",
    };
  }
}

export const aiWorkflowsService = new AIWorkflowsService();


